import React, { useState } from "react"
import { Box, Grid, Typography, Stack } from "@mui/material"
import { PictureAsPdf, InsertDriveFile, Image as ImageIcon } from "@mui/icons-material"
import CommonModal from "components/CommonModal"
import { UploadFieldContainer } from "components/FileUpload/components/FieldContainer"
import AppButton from "components/StyledComponents/AppButton"
import StyledLabel from "components/StyledComponents/StyledLabel"
import CommonDragAndDrop from "components/CommonDragAndDrop"
import RedCloseIcon from "assets/icons/RedCloseIcon.svg"
import { bytesToSize } from "helpers/functions"
import { useDispatch } from "react-redux"
import { uploadPreshiftMediaAction } from "redux/superAdmin/actions/user"

const isImageUrl = (url) => /\.(jpg|jpeg|png|gif|webp|svg)(\?.*)?$/i.test(url || "")

const PreshiftUploadPdfField = ({
  title,
  type,
  value,
  onChange,
  disabled,
  placeholder = "Upload Floor Plan",
  isRequired = false,
  ...props
}) => {
  const [openUploadModal, setOpenUploadModal] = useState(false)
  const [openPreviewModal, setOpenPreviewModal] = useState(false)
  const [selectedFile, setSelectedFile] = useState(null)
  const dispatch = useDispatch()

  const handleOpenUploadModal = () => setOpenUploadModal(true)

  const handleCloseUploadModal = () => {
    setOpenUploadModal(false)
    setSelectedFile(null)
  }

  const displayInfo = value
    ? { url: value?.url || value, name: value?.name, size: value?.size }
    : null

  const onDelete = () => {
    if (!disabled) {
      onChange?.(null)
    }
  }

  const handleUpload = () => {
    if (!selectedFile) return

    let formData = new FormData()
    formData.append("media", selectedFile)
    formData.append("media_type", "IMAGE")
    formData.append("content_type", selectedFile.type || "OTHER")

    dispatch(
      uploadPreshiftMediaAction({
        payload: formData,
        callback: (res) => {
          if (res?.success) {
            onChange?.({
              id: res?.data?.id,
              url: res?.data?.url,
              name: selectedFile.name,
              size: selectedFile.size
            })
            handleCloseUploadModal()
          }
        }
      })
    )
  }

  const getFileIcon = (fileName) => {
    const name = fileName?.toLowerCase() || ""
    if (name.endsWith(".pdf")) return <PictureAsPdf />
    if (name.match(/\.(jpeg|jpg|png|gif|webp)$/)) return <ImageIcon />
    return <InsertDriveFile />
  }

  const showImage = () => {
    const displayValue =
      displayInfo?.name ||
      (typeof displayInfo?.url === "string" ? displayInfo.url.split("/").pop() : "Uploaded File")
    return (
      <Box className="d-flex w-100 h-100 imageDataContainer">
        <Box
          className="imageDataContainerInner flex-grow-1 flex-shrink-1"
          sx={{ cursor: "pointer" }}
          onClick={() => setOpenPreviewModal(true)}>
          <Stack
            direction="row"
            gap={2}
            alignItems="center"
            sx={{ minWidth: 0, overflow: "hidden" }}>
            {getFileIcon(displayValue)}
            <Stack sx={{ minWidth: 0 }}>
              <Typography variant="body2Regular" noWrap>
                {displayValue}
              </Typography>
              {displayInfo?.size ? (
                <Typography variant="tag1" sx={{ color: "#ADB5BD" }} noWrap>
                  {bytesToSize(displayInfo.size)}
                </Typography>
              ) : null}
            </Stack>
          </Stack>
        </Box>
        {!disabled && (
          <img
            className="flex-shrink-0"
            role="button"
            src={RedCloseIcon}
            onClick={onDelete}
            alt="Delete"
            style={{ width: 20 }}
          />
        )}
      </Box>
    )
  }

  const previewUrl = displayInfo?.url

  return (
    <Grid container {...props}>
      {title ? (
        <StyledLabel variant="body1Regular">
          {title}
          {isRequired ? <span>*</span> : null}
        </StyledLabel>
      ) : null}
      <Grid item xs={12}>
        <UploadFieldContainer>
          <Box className="d-flex w-100 h-100 noImageDataContainer justify-content-end">
            <Box className="d-flex" width="100%" sx={{ justifyContent: "flex-end" }}>
              {displayInfo ? (
                showImage()
              ) : disabled ? (
                <Box
                  className="placeholder"
                  sx={{
                    flexGrow: 1,
                    display: "flex",
                    alignItems: "center",
                    pl: 2
                  }}>
                  No file uploaded
                </Box>
              ) : (
                <>
                  <Box
                    className="placeholder"
                    sx={{
                      flexGrow: 1,
                      display: "flex",
                      alignItems: "center",
                      pl: 2,
                      color: "#999"
                    }}>
                    {placeholder}
                  </Box>
                  <AppButton onClick={handleOpenUploadModal}>Upload</AppButton>
                </>
              )}
            </Box>
          </Box>
        </UploadFieldContainer>
      </Grid>

      <CommonModal
        open={openUploadModal}
        onClose={handleCloseUploadModal}
        hideAction={selectedFile ? false : true}
        actionButtons={
          selectedFile ? (
            <Box sx={{ display: "flex", justifyContent: "center", gap: 2, pb: 2 }}>
              <AppButton onClick={() => setSelectedFile(null)}>Replace</AppButton>
              <AppButton onClick={handleUpload}>Submit</AppButton>
            </Box>
          ) : null
        }>
        <Box sx={{ padding: "20px", height: "100%" }}>
          <Typography variant="h2" textAlign="center">
            {title}
          </Typography>
          <Box width={selectedFile ? "90%" : "70%"} margin="0 auto" mt={2}>
            <CommonDragAndDrop
              setDisable={() => {}}
              type={type}
              setAllFilesSelected={() => {}}
              onChange={(url) => {
                setSelectedFile(url)
              }}
              accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
              preview={true}
              selectedFile={selectedFile}
              setSelectedFile={setSelectedFile}
            />
          </Box>
        </Box>
      </CommonModal>

      <CommonModal
        open={openPreviewModal}
        onClose={() => setOpenPreviewModal(false)}
        maxWidth="lg"
        hideAction>
        {isImageUrl(previewUrl) ? (
          <img
            src={previewUrl}
            alt="Preview"
            style={{ width: "100%", maxHeight: "85vh", objectFit: "contain", display: "block" }}
          />
        ) : (
          <iframe
            src={`https://docs.google.com/gview?url=${encodeURIComponent(
              previewUrl
            )}&embedded=true`}
            title="File Preview"
            style={{ width: "100%", height: "85vh", border: "none", display: "block" }}
          />
        )}
      </CommonModal>
    </Grid>
  )
}

export default PreshiftUploadPdfField
